import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { format, subDays } from "date-fns";
import {
  Activity,
  ArrowLeft,
  CalendarCheck2,
  CalendarDays,
  CheckCircle2,
  Clock,
  CreditCard,
  Eye,
  LayoutDashboard,
  Mail,
  MessageSquare,
  Send,
  ShieldCheck,
  Sparkles,
  TrendingUp,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WhatsAppIcon } from "@/components/admin_dashboard/BrandIcons";
import { createSampleCustomers } from "@/lib/customerSamples";

const DEMO_METRICS: Array<{
  label: string;
  value: string;
  valueClassName?: string;
}> = [
  { label: "Status", value: "VIP", valueClassName: "text-violet-600" },
  { label: "Engagement", value: "92.4" },
  { label: "Order value", value: "$840.00" },
  { label: "LTV (paid)", value: "$840.00" },
  { label: "Total bookings", value: "8" },
  { label: "Messages", value: "24" },
  { label: "Preferred channel", value: "WhatsApp" },
  { label: "Spend trend", value: "↑ Growing", valueClassName: "text-emerald-600" },
  { label: "Visit cadence", value: "~28 days" },
  { label: "Usually engages", value: "Weekday evenings" },
];

const ACTIVITY_BARS = [32, 48, 40, 64, 52, 78, 58, 88, 68, 96, 72, 100] as const;

const TAB_TRIGGER_CLASS =
  "flex items-center gap-2 rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 pt-2 text-sm font-semibold text-muted-foreground shadow-none transition-all data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none";

const SampleCustomerProfile = () => {
  const navigate = useNavigate();
  const customer = useMemo(() => createSampleCustomers()[0], []);
  const now = useMemo(() => new Date(), []);
  const activity = useMemo(() => [
    {
      icon: CalendarCheck2,
      title: "Booking completed",
      detail: "Monthly appointment · 60 minutes",
      at: subDays(now, 4),
      color: "text-emerald-600",
    },
    {
      icon: MessageSquare,
      title: "WhatsApp message received",
      detail: "Confirmed the next preferred Thursday evening slot",
      at: subDays(now, 7),
      color: "text-sky-600",
    },
    {
      icon: CreditCard,
      title: "Payment succeeded",
      detail: "$120.00 paid online",
      at: subDays(now, 18),
      color: "text-emerald-600",
    },
    {
      icon: Sparkles,
      title: "Referral recorded",
      detail: "Introduced a new customer who completed their first booking",
      at: subDays(now, 31),
      color: "text-violet-600",
    },
  ], [now]);

  return (
    <div data-testid="sample-customer-profile">
      <div className="mb-5 flex flex-col gap-3 rounded-lg border border-sky-200 bg-sky-50/80 px-4 py-3 text-sky-950 sm:flex-row sm:items-center sm:justify-between" role="status">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
          <div>
            <p className="text-sm font-semibold">Interactive sample profile</p>
            <p className="mt-0.5 text-xs text-sky-900/80">
              This read-only profile shows how Dibs looks when a customer relationship is thriving. No data is saved and no messages are sent.
            </p>
          </div>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 shrink-0 gap-1.5 border-sky-300 bg-white text-sky-950 hover:bg-sky-100"
          onClick={() => navigate("/?tab=customers")}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to customers
        </Button>
      </div>

      <div id="customer-profile-content" className="grid w-full grid-cols-1 gap-6 lg:grid-cols-[380px_1fr] lg:items-start">
        <aside className="space-y-5">
          <div className="space-y-4 py-1">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="font-heading text-xl font-semibold">{customer.name}</h2>
                  <Badge variant="outline" className="border-sky-300 bg-sky-50 text-sky-700">Sample</Badge>
                  <Badge variant="outline" className="border-violet-300 bg-violet-50 text-violet-700">VIP</Badge>
                </div>
                <p className="mt-1 text-xs tracking-wide text-muted-foreground">{customer.short_id}</p>
              </div>
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 font-heading text-sm font-semibold text-primary">
                AT
              </div>
            </div>

            <div className="space-y-3 border-t border-border pt-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Customer details</p>
              <div className="grid grid-cols-[86px_1fr] gap-2 text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground"><Mail className="h-3.5 w-3.5" />Email</span>
                <span className="truncate">{customer.email}</span>
              </div>
              <div className="grid grid-cols-[86px_1fr] gap-2 text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground"><WhatsAppIcon size={14} />Phone</span>
                <span>{customer.phone}</span>
              </div>
              <div className="grid grid-cols-[86px_1fr] gap-2 text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground"><CalendarDays className="h-3.5 w-3.5" />Birthday</span>
                <span>18 March</span>
              </div>
              <div className="grid grid-cols-[86px_1fr] gap-2 text-sm">
                <span className="flex items-center gap-1.5 text-muted-foreground"><Clock className="h-3.5 w-3.5" />Customer</span>
                <span>For 7 months</span>
              </div>
            </div>

            <div className="space-y-2 border-t border-border pt-4">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Groups</p>
              <div className="flex flex-wrap gap-2">
                <Badge variant="secondary">VIP regulars</Badge>
                <Badge variant="secondary">Referral advocates</Badge>
                <Badge variant="secondary">Evening customers</Badge>
              </div>
            </div>
          </div>

          <Card className="shadow-none">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Relationship snapshot</CardTitle>
              <CardDescription>Why this customer is thriving</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {[
                ["6-booking attendance streak", "No cancellations"],
                ["Replies within 2 hours", "on WhatsApp"],
                ["1 successful referral", "High advocacy"],
              ].map(([title, detail]) => (
                <div key={title} className="flex items-start gap-2.5">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  <div>
                    <p className="font-medium">{title}</p>
                    <p className="text-xs text-muted-foreground">{detail}</p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </aside>

        <main className="min-w-0">
          <Tabs defaultValue="overview" className="w-full" data-testid="customer-profile-tabs">
            <TabsList className="mb-4 h-auto w-full justify-start gap-6 overflow-x-auto rounded-none border-b border-border bg-transparent p-0">
              <TabsTrigger value="overview" className={TAB_TRIGGER_CLASS}><LayoutDashboard className="h-4 w-4" />Overview</TabsTrigger>
              <TabsTrigger value="approvals" className={TAB_TRIGGER_CLASS}>
                <ShieldCheck className="h-4 w-4" />Actions queue
                <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">2</span>
              </TabsTrigger>
              <TabsTrigger value="activities" className={TAB_TRIGGER_CLASS}><Activity className="h-4 w-4" />Activities</TabsTrigger>
              <TabsTrigger value="intel" className={TAB_TRIGGER_CLASS}><Eye className="h-4 w-4" />Intel</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="mt-0 space-y-4 border-0 p-0 outline-none">
              <div>
                <p className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">AI insights</p>
                <Card className="border-primary/20 bg-primary/5 shadow-none">
                  <CardContent className="py-4">
                    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-primary">Synopsis</p>
                    <p className="text-sm leading-relaxed text-foreground">
                      Avery is a highly engaged VIP customer with eight completed bookings, $840 in lifetime value, and a growing spend trend. They keep a reliable four-week cadence, prefer weekday evenings, respond quickly on WhatsApp, and recently referred a new customer.
                    </p>
                    <p className="mt-2 text-[11px] text-muted-foreground">Refreshed today · High confidence</p>
                    <div className="mt-4 border-t border-primary/10 pt-4">
                      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-primary">Recommended next step</p>
                      <p className="text-sm text-foreground">
                        Thank Avery for the referral and offer first choice of their usual Thursday evening slot for next month.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {DEMO_METRICS.map((metric) => (
                  <div key={metric.label} className="rounded-lg border bg-card px-6 py-4">
                    <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{metric.label}</p>
                    <p className={`mt-1 text-base font-semibold ${metric.valueClassName ?? ""}`}>{metric.value}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="approvals" className="mt-0 space-y-3 border-0 p-0 outline-none">
              <div>
                <h3 className="font-heading text-base font-semibold">Suggested actions</h3>
                <p className="text-sm text-muted-foreground">Preview how Dibs turns customer intelligence into timely follow-ups.</p>
              </div>
              {[
                {
                  title: "Thank Avery for the successful referral",
                  body: "Send a warm WhatsApp note and include the existing referral reward.",
                  reason: "A referred customer completed their first booking yesterday.",
                },
                {
                  title: "Offer the preferred Thursday evening slot",
                  body: "Give Avery first choice before next month’s schedule fills up.",
                  reason: "Seven of eight bookings were made for weekday evenings.",
                },
              ].map((action) => (
                <Card key={action.title} className="shadow-none">
                  <CardContent className="flex flex-col gap-4 py-4 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="font-medium">{action.title}</p>
                        <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-700">Demo only</Badge>
                      </div>
                      <p className="mt-1 text-sm text-muted-foreground">{action.body}</p>
                      <p className="mt-2 text-xs text-muted-foreground"><span className="font-medium text-foreground">Why now:</span> {action.reason}</p>
                    </div>
                    <Button type="button" size="sm" className="shrink-0 gap-1.5" disabled>
                      <Send className="h-3.5 w-3.5" />Approve
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </TabsContent>

            <TabsContent value="activities" className="mt-0 space-y-4 border-0 p-0 outline-none">
              <Card className="shadow-none">
                <CardHeader>
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <CardTitle className="text-base">Activity over time</CardTitle>
                      <CardDescription>Bookings and conversations across the last 12 weeks</CardDescription>
                    </div>
                    <Badge variant="outline" className="gap-1 border-emerald-300 bg-emerald-50 text-emerald-700"><TrendingUp className="h-3 w-3" />Healthy</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex h-40 items-end gap-2 border-b border-border px-1" aria-label="Activity trend increasing over twelve weeks">
                    {ACTIVITY_BARS.map((height, index) => (
                      <div key={index} className="flex h-full flex-1 items-end">
                        <div className="w-full rounded-t bg-primary/70 transition-colors hover:bg-primary" style={{ height: `${height}%` }} />
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex justify-between text-[10px] text-muted-foreground"><span>12 weeks ago</span><span>This week</span></div>
                </CardContent>
              </Card>

              <Card className="shadow-none">
                <CardHeader><CardTitle className="text-base">Recent activity</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  {activity.map(({ icon: Icon, title, detail, at, color }, index) => (
                    <div key={title} className="relative flex gap-3">
                      {index < activity.length - 1 && <span className="absolute bottom-[-16px] left-[15px] top-8 w-px bg-border" aria-hidden />}
                      <div className="z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full border bg-background">
                        <Icon className={`h-4 w-4 ${color}`} />
                      </div>
                      <div className="min-w-0 flex-1 pt-0.5">
                        <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm font-medium">{title}</p>
                          <p className="text-xs text-muted-foreground">{format(at, "MMM d, yyyy")}</p>
                        </div>
                        <p className="text-xs text-muted-foreground">{detail}</p>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="intel" className="mt-0 space-y-4 border-0 p-0 outline-none">
              <div className="grid gap-4 xl:grid-cols-2">
                <Card className="shadow-none">
                  <CardHeader><CardTitle className="text-base">Known preferences</CardTitle><CardDescription>Signals grounded in bookings and conversations</CardDescription></CardHeader>
                  <CardContent className="flex flex-wrap gap-2">
                    {["Thursday evenings", "60-minute appointments", "WhatsApp reminders", "Books 2–3 weeks ahead"].map((item) => <Badge key={item} variant="secondary">{item}</Badge>)}
                  </CardContent>
                </Card>
                <Card className="shadow-none">
                  <CardHeader><CardTitle className="text-base">Conversation hooks</CardTitle><CardDescription>Useful context for a personal follow-up</CardDescription></CardHeader>
                  <CardContent className="space-y-2 text-sm">
                    <p>• Thank Avery for referring Jamie.</p>
                    <p>• Mention the six-booking attendance streak.</p>
                    <p>• Offer first choice of Thursday evening times.</p>
                  </CardContent>
                </Card>
              </div>
              <Card className="shadow-none">
                <CardHeader className="flex flex-row items-center justify-between space-y-0">
                  <div><CardTitle className="text-base">Evidence and confidence</CardTitle><CardDescription>Why Dibs considers these insights reliable</CardDescription></div>
                  <Badge className="bg-emerald-600 hover:bg-emerald-600">High confidence</Badge>
                </CardHeader>
                <CardContent className="grid gap-3 sm:grid-cols-3">
                  {[
                    ["8", "completed bookings"],
                    ["24", "inbound messages"],
                    ["7", "recent activity signals"],
                  ].map(([value, label]) => (
                    <div key={label} className="rounded-lg bg-muted/50 p-4 text-center"><p className="text-xl font-semibold">{value}</p><p className="mt-1 text-xs text-muted-foreground">{label}</p></div>
                  ))}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </main>
      </div>
    </div>
  );
};

export default SampleCustomerProfile;
